import { useState } from 'react';
import { useSelector } from 'react-redux';

import { Box, ButtonGroup, Card, Heading, Modal, Paragraph, Text } from '@zillow/constellation-10';

import HelmetContainer from '@common/components/HelmetContainer';
import SeoConstants from '@renter-hub/constants/seoConstants';
import faqs from '@renter-hub/constants/FaqConstants';
import FaqComponent from '@common/components/ConstellationFaqComponent';
import NotificationContainer from '@common/components/NotificationContainer';
import DebouncedButton from '@common/components/ConstellationDebouncedButton';
import userPersonas from '@common/constants/userPersonas';
import Calendar from '@common/components/Calendar';
import type { RenterTransactionProps } from '@renter-hub/models/Types';
import { centsToDollars } from '@common/paymentsUtils/currencyUtils';
import type { CreatePaymentSubscriptionWebApiRequestDto } from '@zg-rentals/ts-rent-payment-client';
import { formatDate } from '@common/paymentsUtils/dateUtils';
import { getVerifiedPaymentMethods } from '@common/paymentsUtils/multiplePaymentMethodsUtil';
import type { RootState } from '../../../../store/types';
import AvailablePaymentMethods from '../../components/AvailablePaymentMethods';

interface Props {
  today: Date;
  transaction: RenterTransactionProps;
  failedPaymentEmail: boolean;
  onSubmit: (rentPaymentInfo: CreatePaymentSubscriptionWebApiRequestDto) => any;
  goToDashboard: () => void;
}

export const PayPastDueRentPaymentPage = (props: Props) => {
  const paymentMethods = useSelector((state: RootState) => state.payments.renter.dashboard.paymentMethods);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState(() => {
    if (!paymentMethods?.length) return '';

    // Get verified payment methods using the shared utility
    const verifiedPaymentMethods = getVerifiedPaymentMethods(paymentMethods);

    // Use the first verified payment method
    return verifiedPaymentMethods[0]?.id || '';
  });

  const { today, transaction, failedPaymentEmail } = props;

  const goToDashboard = () => {
    props.goToDashboard();
  };

  const onSubmit = async () => {
    setIsLoading(true);

    const response = await props.onSubmit({
      scheduleMonth: transaction.scheduleMonth,
      // Normalize to midnight UTC via YYYY-MM-DD round-trip to match the date-only
      // timestamp format the backend expects (consistent with other payment flows)
      paymentDate: new Date().setHours(0, 0, 0, 0),
      recurring: false,
      encodedPaymentMethodId: selectedPaymentMethodId,
    });

    const { error } = response;
    if (error) {
      setIsLoading(false);
    }
  };

  const renderCalendarTitle = () => {
    if (failedPaymentEmail) {
      return (
        <Text css={{ color: 'text.feedback.critical.strong' }} textStyle="body-sm">
          We couldn’t process your payment using your payment method. Contact your bank, or try a different payment
          method.
        </Text>
      );
    } else {
      return <Text css={{ color: 'text.feedback.critical.strong' }}>Your rent payment is overdue</Text>;
    }
  };

  const renderRentPaymentSection = () => {
    const showCardFee = transaction.zgFee !== 0;

    return (
      <Box css={{ marginTop: 'loose', marginBottom: 'loose' }}>
        <Card.Root elevated={false}>
          <Card.Header css={{ padding: 'default' }}>
            <Paragraph textStyle="body-bold">Rent payment</Paragraph>
            <Text className="fs-exclude">Due on {formatDate(transaction.dueDate, 'MMMM d, yyyy')}</Text>
          </Card.Header>
          <Card.Body
            css={{
              paddingBottom: 'default',
              paddingRight: 'default',
              paddingLeft: 'default',
            }}
          >
            <Box
              css={{
                display: 'grid',
                gridTemplateColumns: '9fr 3fr',
                gap: 'tighter',
              }}
            >
              <Box css={{ paddingBottom: 'tighter' }}>
                <Text>Rent amount</Text>
              </Box>
              <Box css={{ padding: 'tighter', textAlign: 'right' }}>
                <Text>{centsToDollars(transaction.amount)}</Text>
              </Box>
              {showCardFee && (
                <>
                  <Box css={{ paddingTop: 'tighter', paddingBottom: 'tighter' }}>
                    <Text>Card fee</Text>
                  </Box>
                  <Box css={{ padding: 'tighter', textAlign: 'right' }}>
                    <Text>{centsToDollars(transaction.zgFee)}</Text>
                  </Box>
                </>
              )}
              <Box css={{ paddingTop: 'tighter', paddingBottom: 'tighter' }}>
                <Text textStyle="body-bold">Total payment</Text>
              </Box>
              <Box css={{ padding: 'tighter', textAlign: 'right' }}>
                <Text textStyle="body-bold">{centsToDollars(transaction.totalAmount)}</Text>
              </Box>
            </Box>
          </Card.Body>
        </Card.Root>
      </Box>
    );
  };

  return (
    <Modal
      size="sm"
      open={true}
      trigger={null}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          goToDashboard();
        }
      }}
      header={
        <Box css={{ padding: '16px 48px 16px 16px' }}>
          <Heading level={2} textStyle="heading-xs">
            Your {formatDate(transaction.dueDate, 'MMMM d')} rent is overdue
          </Heading>
        </Box>
      }
      body={
        <Box css={{ padding: 'default' }}>
          <HelmetContainer {...SeoConstants.payPastDueRentPaymentPage} />
          <NotificationContainer persona={userPersonas.RENTER} />

          <Box
            css={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 'default',
              marginTop: 'loose',
            }}
          >
            <Calendar title={'Pay on'} dateLine1={formatDate(today, 'MMM do')} />
            {renderCalendarTitle()}
          </Box>
          {renderRentPaymentSection()}
          <AvailablePaymentMethods
            renderSelect
            onOptionChange={(e) => setSelectedPaymentMethodId(e.target.value)}
            isLoading={isLoading}
            selectedPaymentMethodId={selectedPaymentMethodId}
          />
          <Text textStyle="body-sm">
            Your payment will be processed immediately. If this date falls on a weekend or holiday, we’ll send the
            payment <strong>1 business day later</strong>.
          </Text>
          <Box css={{ marginTop: 'loose' }}>
            <FaqComponent faqs={faqs.PAYMENT_SETTINGS_V2.OVERDUE_SCHEDULING_PAGE.body()} />
          </Box>
        </Box>
      }
      footer={
        <ButtonGroup
          aria-label="action-buttons"
          css={{
            padding: 'default',
          }}
        >
          <DebouncedButton emphasis="filled" onClick={onSubmit} loading={isLoading}>
            {failedPaymentEmail ? 'Try again' : 'Send rent payment now'}
          </DebouncedButton>
        </ButtonGroup>
      }
    />
  );
};

export default PayPastDueRentPaymentPage;
